import type { HREmployee, HRShiftAssignment, HRShiftType } from '@cloud/db'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Clock, Moon, Pencil, Plus, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { orpc } from '@/lib/orpcClient'

// ── Schemas ───────────────────────────────────────────────────────────────────

const shiftTypeSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  startTime: z.string().min(1, 'Start time is required'),
  endTime: z.string().min(1, 'End time is required'),
  breakMinutes: z.number().min(0).optional(),
  isNightShift: z.boolean().optional(),
  color: z.string().max(7).optional(),
})

const assignShiftSchema = z.object({
  employeeId: z.number().min(1, 'Employee is required'),
  shiftTypeId: z.number().min(1, 'Shift type is required'),
  startDate: z.string().min(1, 'Start date is required'),
  endDate: z.string().optional(),
})

type ShiftTypeFormValues = z.infer<typeof shiftTypeSchema>
type AssignShiftFormValues = z.infer<typeof assignShiftSchema>

// ── Route ─────────────────────────────────────────────────────────────────────

export const Route = createFileRoute('/hr/shifts/')({
  component: ShiftsPage,
})

function ShiftsPage() {
  return (
    <div className="container mx-auto py-6 px-4">
      <div className="mb-6">
        <h1 className="text-2xl font-bold">Shifts</h1>
        <p className="text-muted-foreground">Manage shift types and employee assignments</p>
      </div>

      <Tabs defaultValue="types">
        <TabsList className="mb-4">
          <TabsTrigger value="types">Shift Types</TabsTrigger>
          <TabsTrigger value="assignments">Assignments</TabsTrigger>
        </TabsList>
        <TabsContent value="types">
          <ShiftTypesList />
        </TabsContent>
        <TabsContent value="assignments">
          <ShiftAssignmentsList />
        </TabsContent>
      </Tabs>
    </div>
  )
}

// ── Shift Types ───────────────────────────────────────────────────────────────

function ShiftTypesList() {
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [editingShift, setEditingShift] = useState<HRShiftType | null>(null)
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; id: number | null }>({
    open: false,
    id: null,
  })

  const queryClient = useQueryClient()

  const { data: shiftTypes, isLoading } = useQuery(
    orpc.hr.listShiftTypes.queryOptions({ input: {} }),
  )

  const refetch = () => {
    queryClient.invalidateQueries({ queryKey: [['hr', 'listShiftTypes']] })
  }

  const createMutation = useMutation(
    orpc.hr.createShiftType.mutationOptions({
      onSuccess: () => {
        toast.success('Shift type created')
        refetch()
        setIsFormOpen(false)
      },
      onError: (e: Error) => toast.error(e.message),
    }),
  )

  const updateMutation = useMutation(
    orpc.hr.updateShiftType.mutationOptions({
      onSuccess: () => {
        toast.success('Shift type updated')
        refetch()
        setIsFormOpen(false)
        setEditingShift(null)
      },
      onError: (e: Error) => toast.error(e.message),
    }),
  )

  const deleteMutation = useMutation(
    orpc.hr.deleteShiftType.mutationOptions({
      onSuccess: () => {
        toast.success('Shift type deleted')
        refetch()
        setDeleteDialog({ open: false, id: null })
      },
      onError: (e: Error) => toast.error(e.message),
    }),
  )

  const types = (shiftTypes ?? []) as HRShiftType[]

  return (
    <>
      <div className="flex justify-end mb-4">
        <Button
          onClick={() => {
            setEditingShift(null)
            setIsFormOpen(true)
          }}
        >
          <Plus className="h-4 w-4 mr-2" />
          Create Shift Type
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="space-y-2 p-6">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : types.length === 0 ? (
            <div className="text-center py-12">
              <Clock className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No shift types found</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Time Range</TableHead>
                  <TableHead>Break</TableHead>
                  <TableHead>Night Shift</TableHead>
                  <TableHead>Color</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {types.map((shift) => (
                  <TableRow key={shift.id}>
                    <TableCell className="font-medium">{shift.name}</TableCell>
                    <TableCell>
                      {shift.startTime} – {shift.endTime}
                    </TableCell>
                    <TableCell>
                      {shift.breakMinutes != null ? `${shift.breakMinutes} min` : '-'}
                    </TableCell>
                    <TableCell>
                      {shift.isNightShift ? (
                        <Badge variant="outline" className="bg-indigo-100 text-indigo-800">
                          <Moon className="h-3 w-3 mr-1" />
                          Night
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {shift.color ? (
                        <div className="flex items-center gap-2">
                          <div
                            className="h-4 w-4 rounded-full border"
                            style={{ backgroundColor: shift.color }}
                          />
                          <span className="text-xs text-muted-foreground">{shift.color}</span>
                        </div>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setEditingShift(shift)
                            setIsFormOpen(true)
                          }}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-600"
                          onClick={() => setDeleteDialog({ open: true, id: shift.id })}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <ShiftTypeFormDialog
        open={isFormOpen}
        onOpenChange={(open) => {
          setIsFormOpen(open)
          if (!open) setEditingShift(null)
        }}
        shift={editingShift}
        onSubmit={async (data) => {
          if (editingShift) {
            await updateMutation.mutateAsync({ id: editingShift.id, ...data })
          } else {
            await createMutation.mutateAsync(data)
          }
        }}
      />

      <AlertDialog
        open={deleteDialog.open}
        onOpenChange={(open) => setDeleteDialog({ open, id: null })}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Shift Type</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this shift type? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (deleteDialog.id) deleteMutation.mutate({ id: deleteDialog.id })
              }}
              className="bg-red-600 hover:bg-red-700"
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

interface ShiftTypeFormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  shift: HRShiftType | null
  onSubmit: (data: ShiftTypeFormValues) => Promise<void>
}

function ShiftTypeFormDialog({ open, onOpenChange, shift, onSubmit }: ShiftTypeFormDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)

  const form = useForm<ShiftTypeFormValues>({
    resolver: zodResolver(shiftTypeSchema),
    defaultValues: {
      name: shift?.name ?? '',
      startTime: shift?.startTime ?? '',
      endTime: shift?.endTime ?? '',
      breakMinutes: shift?.breakMinutes ?? 0,
      isNightShift: shift?.isNightShift ?? false,
      color: shift?.color ?? '',
    },
  })

  // Reset when shift changes
  const handleOpenChange = (open: boolean) => {
    if (open && shift) {
      form.reset({
        name: shift.name,
        startTime: shift.startTime,
        endTime: shift.endTime,
        breakMinutes: shift.breakMinutes ?? 0,
        isNightShift: shift.isNightShift ?? false,
        color: shift.color ?? '',
      })
    } else if (!open) {
      form.reset({
        name: '',
        startTime: '',
        endTime: '',
        breakMinutes: 0,
        isNightShift: false,
        color: '',
      })
    }
    onOpenChange(open)
  }

  const handleSubmit = async (data: ShiftTypeFormValues) => {
    setIsSubmitting(true)
    try {
      await onSubmit(data)
      form.reset()
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{shift ? 'Edit Shift Type' : 'Create Shift Type'}</DialogTitle>
          <DialogDescription>
            {shift ? 'Update shift type details' : 'Define a new shift type'}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name *</FormLabel>
                  <FormControl>
                    <Input placeholder="Morning Shift" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="startTime"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Start Time *</FormLabel>
                    <FormControl>
                      <Input type="time" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="endTime"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>End Time *</FormLabel>
                    <FormControl>
                      <Input type="time" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="breakMinutes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Break (minutes)</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      min={0}
                      placeholder="30"
                      value={field.value?.toString() ?? ''}
                      onChange={(e) =>
                        field.onChange(e.target.value ? Number(e.target.value) : 0)
                      }
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="color"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Color</FormLabel>
                  <FormControl>
                    <div className="flex items-center gap-2">
                      <Input
                        type="color"
                        className="w-12 h-9 p-1 cursor-pointer"
                        value={field.value || '#6366f1'}
                        onChange={(e) => field.onChange(e.target.value)}
                      />
                      <Input
                        placeholder="#6366f1"
                        value={field.value ?? ''}
                        onChange={(e) => field.onChange(e.target.value)}
                        className="flex-1"
                        maxLength={7}
                      />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="isNightShift"
              render={({ field }) => (
                <FormItem className="flex items-center gap-2">
                  <FormControl>
                    <Checkbox
                      checked={field.value ?? false}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <FormLabel className="!mt-0 cursor-pointer">Night Shift</FormLabel>
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Saving...' : shift ? 'Update Shift' : 'Create Shift'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}

// ── Shift Assignments ─────────────────────────────────────────────────────────

function ShiftAssignmentsList() {
  const [isAssignOpen, setIsAssignOpen] = useState(false)

  const queryClient = useQueryClient()

  const { data: assignmentsData, isLoading } = useQuery(
    orpc.hr.listShiftAssignments.queryOptions({ input: {} }),
  )

  const { data: employeesData } = useQuery(
    orpc.hr.getEmployees.queryOptions({ input: { page: 1, limit: 500 } }),
  )

  const { data: shiftTypesData } = useQuery(
    orpc.hr.listShiftTypes.queryOptions({ input: {} }),
  )

  const refetch = () => {
    queryClient.invalidateQueries({ queryKey: [['hr', 'listShiftAssignments']] })
  }

  const assignMutation = useMutation(
    orpc.hr.assignShift.mutationOptions({
      onSuccess: () => {
        toast.success('Shift assigned')
        refetch()
        setIsAssignOpen(false)
      },
      onError: (e: Error) => toast.error(e.message),
    }),
  )

  const removeMutation = useMutation(
    orpc.hr.removeShiftAssignment.mutationOptions({
      onSuccess: () => {
        toast.success('Shift assignment removed')
        refetch()
      },
      onError: (e: Error) => toast.error(e.message),
    }),
  )

  const assignments = (assignmentsData ?? []) as HRShiftAssignment[]
  const employees = (employeesData?.data ?? []) as HREmployee[]
  const shiftTypes = (shiftTypesData ?? []) as HRShiftType[]

  const getEmployeeName = (id: number) => {
    const emp = employees.find((e) => e.id === id)
    return emp ? `${emp.firstName} ${emp.lastName}` : `Employee #${id}`
  }

  const getShiftName = (id: number) => {
    const shift = shiftTypes.find((s) => s.id === id)
    return shift?.name ?? `Shift #${id}`
  }

  return (
    <>
      <div className="flex justify-end mb-4">
        <Button onClick={() => setIsAssignOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Assign Shift
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="space-y-2 p-6">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : assignments.length === 0 ? (
            <div className="text-center py-12">
              <Clock className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No shift assignments found</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Employee</TableHead>
                  <TableHead>Shift Type</TableHead>
                  <TableHead>Start Date</TableHead>
                  <TableHead>End Date</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {assignments.map((assignment) => {
                  const a = assignment as HRShiftAssignment & {
                    startDate?: string
                    endDate?: string | null
                  }
                  return (
                    <TableRow key={a.id}>
                      <TableCell className="font-medium">
                        {getEmployeeName(a.employeeId)}
                      </TableCell>
                      <TableCell>{getShiftName(a.shiftTypeId)}</TableCell>
                      <TableCell>
                        {a.startDate ? new Date(a.startDate).toLocaleDateString() : '-'}
                      </TableCell>
                      <TableCell>
                        {a.endDate ? (
                          new Date(a.endDate).toLocaleDateString()
                        ) : (
                          <Badge variant="outline" className="bg-green-100 text-green-800">
                            Ongoing
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-600"
                          onClick={() => removeMutation.mutate({ id: a.id })}
                          disabled={removeMutation.isPending}
                        >
                          End Assignment
                        </Button>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <AssignShiftDialog
        open={isAssignOpen}
        onOpenChange={setIsAssignOpen}
        employees={employees}
        shiftTypes={shiftTypes}
        onSubmit={async (data) => {
          await assignMutation.mutateAsync(data)
        }}
      />
    </>
  )
}

interface AssignShiftDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  employees: HREmployee[]
  shiftTypes: HRShiftType[]
  onSubmit: (data: AssignShiftFormValues) => Promise<void>
}

function AssignShiftDialog({
  open,
  onOpenChange,
  employees,
  shiftTypes,
  onSubmit,
}: AssignShiftDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)

  const form = useForm<AssignShiftFormValues>({
    resolver: zodResolver(assignShiftSchema),
    defaultValues: {
      startDate: '',
      endDate: '',
    },
  })

  const handleSubmit = async (data: AssignShiftFormValues) => {
    setIsSubmitting(true)
    try {
      await onSubmit({
        ...data,
        endDate: data.endDate || undefined,
      })
      form.reset()
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>Assign Shift</DialogTitle>
          <DialogDescription>Assign a shift type to an employee</DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="employeeId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Employee *</FormLabel>
                  <Select
                    onValueChange={(v) => field.onChange(Number(v))}
                    value={field.value?.toString() ?? ''}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select employee" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {employees.map((emp) => (
                        <SelectItem key={emp.id} value={emp.id.toString()}>
                          {emp.firstName} {emp.lastName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="shiftTypeId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Shift Type *</FormLabel>
                  <Select
                    onValueChange={(v) => field.onChange(Number(v))}
                    value={field.value?.toString() ?? ''}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select shift type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {shiftTypes.map((shift) => (
                        <SelectItem key={shift.id} value={shift.id.toString()}>
                          {shift.name} ({shift.startTime} – {shift.endTime})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="startDate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Start Date *</FormLabel>
                  <FormControl>
                    <Input type="date" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="endDate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>End Date (optional)</FormLabel>
                  <FormControl>
                    <Input type="date" {...field} value={field.value ?? ''} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Assigning...' : 'Assign Shift'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
